import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { DEDUCTION_CATEGORY_BLURBS, DEDUCTION_CATEGORY_LABELS } from '../categories';
import { deductionsGateway } from '../index';
import type { DeductionCatalogItem } from '../types';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type State = { kind: 'loading' } | { kind: 'ready'; items: DeductionCatalogItem[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

/**
 * People > Deductions > Taxes & Statutory Deductions: a READ-ONLY list of the system-calculated tax and statutory deductions. There is deliberately no
 * control to create, edit, override, hide or delete one, anywhere on this page: the server has no endpoint for it either (contract section 5), and a
 * company-created definition in this category is refused (400 PROTECTED_CATEGORY). Tax profiles and limit rules change only through controlled releases.
 */
export function TaxesStatutoryPage() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    deductionsGateway.listCatalog({ category: 'taxes-statutory' }, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setState({ kind: 'ready', items }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the taxes and statutory deductions.', 'deduction') });
      });
    return () => controller.abort();
  }, [nonce]);

  const retry = useCallback(() => { setState({ kind: 'loading' }); setNonce((n) => n + 1); }, []);

  return (
    <section className="card card--wide jr-page bd-page">
      <div className="section-header">
        <h2>{DEDUCTION_CATEGORY_LABELS['taxes-statutory']}</h2>
      </div>
      <p className="muted bd-lead">{DEDUCTION_CATEGORY_BLURBS['taxes-statutory']}</p>
      <p className="bd-notice bd-notice--info" role="note" data-testid="statutory-note">
        These taxes and statutory deductions are system definitions, calculated by the system. You cannot create, change, override or delete them here. Their tax
        profiles and limits change only through controlled releases that Escochex reviews.
      </p>

      {state.kind === 'loading' && <p className="muted" role="status">Loading{'…'}</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={retry} />}
      {state.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view deductions (deductioncodes.read)" />}
      {state.kind === 'ready' && state.items.length === 0 && <p className="muted" data-testid="statutory-empty">No taxes or statutory deductions are listed.</p>}
      {state.kind === 'ready' && state.items.length > 0 && (
        <ul className="bd-catalog" aria-label="Taxes and statutory deductions">
          {state.items.map((item) => (
            <li key={item.code} className="bd-catalog__item">
              <h3 className="bd-catalog__name">{item.name}</h3>
              <p className="bd-catalog__text">{item.description}</p>
              <div className="bd-catalog__tags">
                <span className="bd-tag">Statutory</span>
                <span className="bd-tag">Calculated by the system</span>
                <span className="bd-tag">Read only</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
